import { PSBErrors } from "@/constants/errors/product-slot-booking";
import BookingModule from "@/store/modules/booking";
import CustomersModule from "@/store/modules/customers";
import { toggleModalVisible } from "./modal";
import { ModalStatus, SendEmailModalProps } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import { LogService } from "@/services/logging";

const INDEX_LOOKUP_TIMEOUT_MS = 2000;

/**
 * if no customer assigned to the selected booking then we show Customers modal
 * else, we show editCustomer modal
 */
export async function assignOrEditCustomer(
  referrer?: ModalStatus,
  props?: SendEmailModalProps
): Promise<void> {
  if (!BookingModule.selectedBooking) {
    throw new Error(PSBErrors.HasNoUUID);
  }
  const selectedCustomer = CustomersModule.selectedCustomers.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (c: any) => c.bookingUuid === BookingModule.selectedBookingUUID
  );
  if (referrer) {
    ModalModule.setModalProps({ goBack: true, referrer, props });
  }
  if (!selectedCustomer) {
    // No customer assigned
    toggleModalVisible(ModalStatus.Customers);
  } else {
    // there is a customer assigned, lets enable user to edit it
    const {
      country,
      email,
      fullName,
      phone,
      id,
      checkOrder,
      city,
      zipCode,
      company,
      address,
      allergies,
      language,
      firstName,
      lastName,
      title,
      emailMarketingOrganiser,
      birthDate,
    } = selectedCustomer;

    // if language is fetched from graphql then it's an object containing the code,
    // if set from POS then it's a string value of the language code
    let languageCode = language;
    if (language?.code) {
      languageCode = language.code;
    }

    // The search index stores the alpha-2 code the country select needs.
    // Resolve it there, while keeping the booking value on any failure.
    const countryCode = await resolveIndexedCountryCode(id, email, country);

    CustomersModule.setEditedCustomers([
      {
        country: countryCode,
        email,
        fullName,
        phone,
        id,
        checkOrder,
        city,
        zipCode,
        company,
        address,
        allergies,
        language: languageCode,
        firstName,
        lastName,
        title,
        emailMarketingOrganiser,
        birthDate,
      },
    ]);
    toggleModalVisible(ModalStatus.EditCustomer);
  }
}

// Falls back to the given value on any lookup failure, timeout or index miss.
async function resolveIndexedCountryCode(
  id: string | number,
  email: string,
  fallback: string
): Promise<string> {
  if (!email) {
    return fallback;
  }

  try {
    const indexed = await Promise.race([
      CustomersModule.fetchIndexedCustomer({ id, email }),
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), INDEX_LOOKUP_TIMEOUT_MS)
      ),
    ]);

    return indexed?.country || fallback;
  } catch (error) {
    LogService.info(
      "Could not resolve customer country from the search index",
      {
        params: { id, error },
      }
    );

    return fallback;
  }
}
